import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/reset.css';
import '../styles/theme.css';
import '../styles/admin.css';

const API_BASE = '%%API_BASE%%';

type Tab = 'overview' | 'inventory' | 'shipments' | 'invoices' | 'customers' | 'formulas' | 'support';

type OverviewData = {
  inventory: { total: number; lowStock: number; outOfStock: number; onHold: number };
  shipments: { awaitingFulfillment: number; inTransit: number; delayed: number; deliveredThisWeek: number };
  invoices: { open: number; overdue: number; outstandingCents: number };
  customers: { active: number; paused: number; newThisWeek: number };
};

type InventoryItem = {
  id: string;
  sku: string;
  formulaId?: string | null;
  formulaName: string;
  formulaCategory?: string | null;
  formulaPattern?: string | null;
  batch?: string | null;
  stockOnHand: number;
  reorderLevel: number;
  units?: string | null;
  status: string;
  updatedAt?: string | null;
};

type Shipment = {
  id: string;
  orderId?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  status?: string | null;
  carrier?: string | null;
  trackingNumber?: string | null;
  shippedAt?: string | null;
  expectedDelivery?: string | null;
  deliveredAt?: string | null;
  notes?: string | null;
};

type Invoice = {
  id: string;
  customerId?: string | null;
  customerName?: string | null;
  amountCents: number;
  currency?: string | null;
  status?: string | null;
  issuedAt?: string | null;
  dueAt?: string | null;
  paidAt?: string | null;
  memo?: string | null;
};

type AdminCustomer = {
  id: string;
  userId?: string | null;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  subscriptionTier?: string | null;
  status?: string | null;
  lastIntakeAt?: string | null;
  createdAt?: string | null;
  outstandingBalanceCents: number;
  activeShipments: number;
  overdueInvoices: number;
};

type Role = 'admin' | 'moderator' | 'customer' | '';

type Supplier = {
  id: string;
  name: string;
  contactEmail?: string | null;
  phone?: string | null;
  originRegion?: string | null;
  certifications?: string | null;
  notes?: string | null;
  lastAuditAt?: string | null;
};

type Formula = {
  id: string;
  slug?: string | null;
  name: string;
  category?: string | null;
  pattern?: string | null;
  tagline?: string | null;
  description?: string | null;
  recommendedTier?: string | null;
  stockStatus?: string | null;
  supplierId?: string | null;
  supplierName?: string | null;
  heroIngredients: string[];
  caution?: string | null;
};

type SupportTicket = {
  id: string;
  customerId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  subject: string;
  status: string;
  priority: string;
  category?: string | null;
  assignedTo?: string | null;
  lastReplyAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastMessage?: string | null;
};

type SupportMessage = {
  id: string;
  ticketId: string;
  authorId?: string | null;
  authorRole?: string | null;
  body: string;
  visibility: string;
  createdAt?: string | null;
};

const shipmentStatuses = [
  { value: 'awaiting_fulfillment', label: 'Awaiting fulfilment' },
  { value: 'prepping', label: 'Prepping' },
  { value: 'packed', label: 'Packed' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'delivered', label: 'Delivered' }
];

const inventoryStatuses = [
  { value: 'healthy', label: 'Healthy' },
  { value: 'low', label: 'Low' },
  { value: 'out-of-stock', label: 'Out of stock' },
  { value: 'on-hold', label: 'On hold' }
];

const invoiceStatuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' }
];

const centsToCurrency = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents || 0) / 100);

const formatDate = (iso?: string | null) => {
  if (!iso) return '–';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const AdminApp = (): JSX.Element => {
  const [token, setToken] = useState<string>(() => localStorage.getItem('soupcon_admin_token') || '');
  const [role, setRole] = useState<Role>(() => (localStorage.getItem('soupcon_admin_role') as Role) || '');
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [banner, setBanner] = useState<string>('');

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketDetail, setTicketDetail] = useState<{ ticket: SupportTicket; messages: SupportMessage[] } | null>(null);

  const [ticketFilters, setTicketFilters] = useState({ status: 'open', priority: '' });
  const [newTicket, setNewTicket] = useState({ subject: '', customerId: '', category: '', priority: 'normal' });
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [newResponse, setNewResponse] = useState('');

  const statusOptions = ['open', 'in_progress', 'pending_customer', 'resolved'];
  const priorityOptions = ['high', 'normal', 'low'];
  const visibilityOptions = ['public', 'internal'];

  const [newSupplier, setNewSupplier] = useState({ name: '', contactEmail: '', originRegion: '' });
  const [newFormula, setNewFormula] = useState({
    name: '',
    slug: '',
    supplierId: '',
    category: '',
    pattern: '',
    tagline: '',
    recommendedTier: 'foundations',
    stockStatus: 'active',
    heroIngredients: '',
    caution: ''
  });

  const canEditInventory = role === 'admin';
  const canEditInvoices = role === 'admin';
  const canEditShipments = role === 'admin' || role === 'moderator';
  const canEditFormulas = role === 'admin';
  const canManageSupport = role === 'admin' || role === 'moderator';

  const logout = useCallback(() => {
    localStorage.removeItem('soupcon_admin_token');
    localStorage.removeItem('soupcon_admin_role');
    setToken('');
    setRole('');
    setOverview(null);
    setInventory([]);
    setShipments([]);
    setInvoices([]);
    setCustomers([]);
    setBanner('');
  }, []);

  const fetchWithAuth = useCallback(
    async (path: string, init?: RequestInit) => {
      if (!token) throw new Error('Authentication required');
      const headers = new Headers(init?.headers || {});
      headers.set('Authorization', `Bearer ${token}`);
      if (init?.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
      let data: any = null;
      try {
        data = await response.json();
      } catch (err) {
        data = null;
      }
      if (response.status === 401) {
        logout();
        throw new Error('Session expired. Please log in again.');
      }
      if (!response.ok) {
        const message = (data && (data.error || data.message)) || response.statusText || 'Request failed';
        throw new Error(message);
      }
      return data ?? {};
    },
    [token, logout]
  );

  const loadTab = useCallback(
    async (tab: Tab) => {
      if (!token || (role !== 'admin' && role !== 'moderator')) return;
      setLoading(true);
      setError('');
      try {
        if (tab === 'overview') {
          const data = await fetchWithAuth('/admin/overview');
          setOverview(data as OverviewData);
        } else if (tab === 'inventory') {
          const data = await fetchWithAuth('/admin/inventory');
          setInventory((data.items || []) as InventoryItem[]);
        } else if (tab === 'shipments') {
          const data = await fetchWithAuth('/admin/shipments');
          setShipments((data.shipments || []) as Shipment[]);
        } else if (tab === 'invoices') {
          const data = await fetchWithAuth('/admin/invoices');
          setInvoices((data.invoices || []) as Invoice[]);
        } else if (tab === 'customers') {
          const data = await fetchWithAuth('/admin/customers');
          setCustomers((data.customers || []) as AdminCustomer[]);
        } else if (tab === 'formulas') {
          const [formulaData, supplierData] = await Promise.all([
            fetchWithAuth('/admin/formulas'),
            fetchWithAuth('/admin/suppliers')
          ]);
          setFormulas((formulaData.formulas || []) as Formula[]);
          setSuppliers((supplierData.suppliers || []) as Supplier[]);
        } else if (tab === 'support') {
          const query = new URLSearchParams();
          if (ticketFilters.status) query.append('status', ticketFilters.status);
          if (ticketFilters.priority) query.append('priority', ticketFilters.priority);
          const data = await fetchWithAuth(`/admin/support/tickets?${query.toString()}`);
          setTickets((data.tickets || []) as SupportTicket[]);
          setTicketDetail(null);
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithAuth, role, token, ticketFilters]
  );

  // Bootstrap session
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const init = async () => {
      setError('');
      try {
        const profile = await fetchWithAuth('/auth/me');
        if (cancelled) return;
        const normalized = (profile.role || 'customer').toLowerCase() as Role;
        setRole(normalized);
        localStorage.setItem('soupcon_admin_role', normalized);
        if (normalized !== 'admin' && normalized !== 'moderator') {
          setError('This account does not have console access.');
          return;
        }
        await loadTab(activeTab);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [token, fetchWithAuth, loadTab, activeTab]);

  // Refresh when tab changes
  useEffect(() => {
    if (!token || !role || (role !== 'admin' && role !== 'moderator')) return;
    loadTab(activeTab);
  }, [activeTab, loadTab, role, token]);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = String(formData.get('email') || '').trim();
    const password = String(formData.get('password') || '').trim();
    if (!email || !password) {
      setError('Enter email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }
      setToken(data.token);
      localStorage.setItem('soupcon_admin_token', data.token);
      const normalized = (data.role || 'customer').toLowerCase();
      setRole(normalized);
      localStorage.setItem('soupcon_admin_role', normalized);
      setBanner('Signed in successfully');
      setTimeout(() => setBanner(''), 4000);
    } catch (err) {
      setError((err as Error).message);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const adjustInventory = async (item: InventoryItem) => {
    const input = prompt(`Adjust stock for ${item.formulaName}`, String(item.stockOnHand));
    if (input === null) return;
    const value = Number.parseInt(input, 10);
    if (!Number.isFinite(value) || value < 0) {
      setError('Please enter a valid non-negative number.');
      return;
    }
    try {
      await fetchWithAuth(`/admin/inventory/${encodeURIComponent(item.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ stockOnHand: value })
      });
      setBanner('Inventory updated');
      setTimeout(() => setBanner(''), 4000);
      loadTab('inventory');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const changeInventoryStatus = async (itemId: string, status: string) => {
    try {
      await fetchWithAuth(`/admin/inventory/${encodeURIComponent(itemId)}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      loadTab('inventory');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const changeInventoryFormula = async (itemId: string, formulaId: string) => {
    try {
      await fetchWithAuth(`/admin/inventory/${encodeURIComponent(itemId)}`, {
        method: 'PUT',
        body: JSON.stringify({ formulaId })
      });
      loadTab('inventory');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const changeShipmentStatus = async (shipmentId: string, status: string) => {
    try {
      await fetchWithAuth(`/admin/shipments/${encodeURIComponent(shipmentId)}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      loadTab('shipments');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateShipmentCarrier = async (shipment: Shipment) => {
    const carrier = prompt('Carrier', shipment.carrier || '');
    if (carrier === null) return;
    const tracking = prompt('Tracking number', shipment.trackingNumber || '') ?? shipment.trackingNumber;
    try {
      await fetchWithAuth(`/admin/shipments/${encodeURIComponent(shipment.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ carrier, trackingNumber: tracking })
      });
      loadTab('shipments');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const markInvoiceStatus = async (invoice: Invoice, status: string) => {
    try {
      await fetchWithAuth(`/admin/invoices/${encodeURIComponent(invoice.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      loadTab('invoices');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const inventoryMap = useMemo(() => inventoryStatuses.reduce<Record<string, string>>((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {}), []);

  const shipmentStatusMap = useMemo(() => shipmentStatuses.reduce<Record<string, string>>((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {}), []);

  const invoiceStatusMap = useMemo(() => invoiceStatuses.reduce<Record<string, string>>((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {}), []);

  const formulaStatusMap: Record<string, string> = useMemo(() => ({
    active: 'Active',
    backorder: 'Backorder',
    'qa-hold': 'QA hold',
    retired: 'Retired'
  }), []);

  const supplierOptions = useMemo(() => suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })), [suppliers]);

  const formulaOptions = useMemo(
    () => formulas.map((formula) => ({ value: formula.id, label: formula.name })),
    [formulas]
  );

  const submitNewSupplier = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newSupplier.name.trim()) {
      setError('Supplier name required');
      return;
    }
    try {
      await fetchWithAuth('/admin/suppliers', {
        method: 'POST',
        body: JSON.stringify(newSupplier)
      });
      setNewSupplier({ name: '', contactEmail: '', originRegion: '' });
      loadTab('formulas');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const submitNewFormula = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newFormula.name.trim()) {
      setError('Formula name required');
      return;
    }
    const heroIngredients = newFormula.heroIngredients
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    try {
      await fetchWithAuth('/admin/formulas', {
        method: 'POST',
        body: JSON.stringify({
          ...newFormula,
          heroIngredients
        })
      });
      setNewFormula({
        name: '',
        slug: '',
        supplierId: '',
        category: '',
        pattern: '',
        tagline: '',
        recommendedTier: 'foundations',
        stockStatus: 'active',
        heroIngredients: '',
        caution: ''
      });
      loadTab('formulas');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateFormulaStatus = async (formula: Formula, status: string) => {
    try {
      await fetchWithAuth(`/admin/formulas/${encodeURIComponent(formula.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ stockStatus: status })
      });
      loadTab('formulas');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateFormulaCaution = async (formula: Formula) => {
    const next = prompt('Update caution / guardrails', formula.caution || '') ?? formula.caution;
    try {
      await fetchWithAuth(`/admin/formulas/${encodeURIComponent(formula.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ caution: next || '' })
      });
      loadTab('formulas');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateFormulaHero = async (formula: Formula) => {
    const next = prompt('Hero ingredients (comma separated)', formula.heroIngredients.join(', ')) ?? formula.heroIngredients.join(', ');
    try {
      const heroIngredients = next.split(',').map((entry) => entry.trim()).filter(Boolean);
      await fetchWithAuth(`/admin/formulas/${encodeURIComponent(formula.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ heroIngredients })
      });
      loadTab('formulas');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateFormulaSupplier = async (formula: Formula, supplierId: string) => {
    try {
      await fetchWithAuth(`/admin/formulas/${encodeURIComponent(formula.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ supplierId })
      });
      loadTab('formulas');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const refreshTickets = () => loadTab('support');

  const loadTicketDetail = async (ticketId: string) => {
    try {
      const data = await fetchWithAuth(`/admin/support/tickets/${encodeURIComponent(ticketId)}`);
      setTicketDetail({ ticket: data.ticket as SupportTicket, messages: (data.messages || []) as SupportMessage[] });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateTicket = async (ticketId: string, updates: Partial<SupportTicket>) => {
    try {
      await fetchWithAuth(`/admin/support/tickets/${encodeURIComponent(ticketId)}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      refreshTickets();
      if (ticketDetail && ticketDetail.ticket.id === ticketId) {
        loadTicketDetail(ticketId);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const postTicketReply = async (ticketId: string, body: string, visibility: string) => {
    if (!body.trim()) {
      setError('Reply cannot be empty');
      return;
    }
    try {
      await fetchWithAuth(`/admin/support/tickets/${encodeURIComponent(ticketId)}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body, visibility })
      });
      setNewResponse('');
      loadTicketDetail(ticketId);
      refreshTickets();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const submitNewTicket = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newTicket.subject.trim()) {
      setError('Ticket subject required');
      return;
    }
    try {
      await fetchWithAuth('/admin/support/tickets', {
        method: 'POST',
        body: JSON.stringify({
          ...newTicket,
          initialMessage: newTicketMessage
        })
      });
      setNewTicket({ subject: '', customerId: '', category: '', priority: 'normal' });
      setNewTicketMessage('');
      refreshTickets();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (!token) {
    return (
      <div className="admin-login-screen">
        <div className="admin-login-card">
          <h1>Soupcon Ops Console</h1>
          <p className="admin-login-copy">Sign in to manage inventory, fulfilment, invoices, and customer care.</p>
          {error && <div className="admin-banner error">{error}</div>}
          <form onSubmit={handleLogin} className="admin-login-form">
            <label htmlFor="admin-email">Work email</label>
            <input id="admin-email" name="email" type="email" placeholder="admin@soupcon.app" required autoComplete="email" />
            <label htmlFor="admin-password">Password</label>
            <input id="admin-password" name="password" type="password" placeholder="Enter your password" required autoComplete="current-password" />
            <button type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</button>
          </form>
          <div className="admin-login-hint">
            <p>Use the seeded credentials:</p>
            <code>admin@soupcon.app / AdminSoupcon!23</code>
            <code>moderator@soupcon.app / ModeratorSoupcon!23</code>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <span className="admin-pill">Internal</span>
          <h1>Soupcon operations console</h1>
          <p>Coordinate formula inventory, branch shipments, billing, and subscriber care.</p>
        </div>
        <div className="admin-header-meta">
          <span className="admin-role">Signed in as {role}</span>
          <button onClick={logout}>Sign out</button>
        </div>
      </header>

      <nav className="admin-nav" aria-label="Admin navigation">
        {(['overview', 'inventory', 'shipments', 'invoices', 'customers', 'formulas', 'support'] as Tab[]).map((tab) => (
          <button
            key={tab}
            className={tab === activeTab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'inventory' && 'Inventory'}
            {tab === 'shipments' && 'Shipments'}
            {tab === 'invoices' && 'Invoices'}
            {tab === 'customers' && 'Customers'}
            {tab === 'formulas' && 'Formulas'}
            {tab === 'support' && 'Support'}
          </button>
        ))}
      </nav>

      {banner && <div className="admin-banner info">{banner}</div>}
      {error && <div className="admin-banner error">{error}</div>}

      <main className="admin-content">
        {loading ? (
          <div className="admin-loading">Loading data…</div>
        ) : (
          <>
            {activeTab === 'overview' && overview && (
              <section className="admin-grid">
                <article className="admin-card">
                  <h3>Inventory health</h3>
                  <ul>
                    <li><strong>{overview.inventory.total}</strong> total SKUs tracked</li>
                    <li><strong>{overview.inventory.lowStock}</strong> trending low</li>
                    <li><strong>{overview.inventory.outOfStock}</strong> out of stock</li>
                    <li><strong>{overview.inventory.onHold}</strong> on QA hold</li>
                  </ul>
                </article>
                <article className="admin-card">
                  <h3>Fulfilment pipeline</h3>
                  <ul>
                    <li><strong>{overview.shipments.awaitingFulfillment}</strong> awaiting fulfilment</li>
                    <li><strong>{overview.shipments.inTransit}</strong> currently in transit</li>
                    <li><strong>{overview.shipments.delayed}</strong> delayed interventions</li>
                    <li><strong>{overview.shipments.deliveredThisWeek}</strong> delivered this week</li>
                  </ul>
                </article>
                <article className="admin-card">
                  <h3>Billing</h3>
                  <ul>
                    <li><strong>{overview.invoices.open}</strong> open invoices</li>
                    <li><strong>{overview.invoices.overdue}</strong> overdue follow-ups</li>
                    <li><strong>{centsToCurrency(overview.invoices.outstandingCents)}</strong> outstanding balance</li>
                  </ul>
                </article>
                <article className="admin-card">
                  <h3>Customers</h3>
                  <ul>
                    <li><strong>{overview.customers.active}</strong> active concierge members</li>
                    <li><strong>{overview.customers.paused}</strong> paused subscriptions</li>
                    <li><strong>{overview.customers.newThisWeek}</strong> new onboardings this week</li>
                  </ul>
                </article>
              </section>
            )}

            {activeTab === 'inventory' && (
              <section className="admin-stack">
                {canEditInventory && (
                  <p className="admin-hint">Adjust stock or update status to broadcast to procurement.</p>
                )}
                <div className="admin-table">
                  {inventory.map((item) => (
                    <div className="admin-card" key={item.id}>
                      <header className="card-header">
                        <h3>{item.formulaName}</h3>
                        <span className={`status-pill status-${item.status}`}>{inventoryMap[item.status] || item.status}</span>
                      </header>
                      <div className="card-body">
                        <dl>
                          <div><dt>SKU</dt><dd>{item.sku}</dd></div>
                          {item.formulaCategory && <div><dt>Category</dt><dd>{item.formulaCategory}</dd></div>}
                          {item.formulaPattern && <div><dt>Pattern</dt><dd>{item.formulaPattern}</dd></div>}
                          <div><dt>Batch</dt><dd>{item.batch || '—'}</dd></div>
                          <div><dt>On hand</dt><dd>{item.stockOnHand} {item.units || 'units'}</dd></div>
                          <div><dt>Reorder at</dt><dd>{item.reorderLevel}</dd></div>
                          <div><dt>Updated</dt><dd>{formatDate(item.updatedAt)}</dd></div>
                        </dl>
                      </div>
                      {canEditInventory && (
                        <footer className="card-footer">
                          <button onClick={() => adjustInventory(item)}>Adjust stock</button>
                          <label>
                            <span>Status</span>
                            <select value={item.status} onChange={(event) => changeInventoryStatus(item.id, event.target.value)}>
                              {inventoryStatuses.map((status) => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>Formula</span>
                            <select
                              value={item.formulaId || ''}
                              onChange={(event) => changeInventoryFormula(item.id, event.target.value)}
                            >
                              <option value="">Unlinked</option>
                              {formulaOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>
                        </footer>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'shipments' && (
              <section className="admin-stack">
                <div className="admin-table">
                  {shipments.map((shipment) => (
                    <div className="admin-card" key={shipment.id}>
                      <header className="card-header">
                        <h3>Order {shipment.orderId || shipment.id}</h3>
                        <span className={`status-pill status-${shipment.status}`}>{shipmentStatusMap[shipment.status || ''] || shipment.status || 'unknown'}</span>
                      </header>
                      <div className="card-body">
                        <dl>
                          <div><dt>Customer</dt><dd>{shipment.customerName || '—'} {shipment.customerEmail ? `(${shipment.customerEmail})` : ''}</dd></div>
                          <div><dt>Carrier</dt><dd>{shipment.carrier || '—'}</dd></div>
                          <div><dt>Tracking</dt><dd>{shipment.trackingNumber || '—'}</dd></div>
                          <div><dt>Ship date</dt><dd>{formatDate(shipment.shippedAt)}</dd></div>
                          <div><dt>Expected</dt><dd>{formatDate(shipment.expectedDelivery)}</dd></div>
                        </dl>
                      </div>
                      {canEditShipments && (
                        <footer className="card-footer">
                          <label>
                            <span>Status</span>
                            <select value={shipment.status || ''} onChange={(event) => changeShipmentStatus(shipment.id, event.target.value)}>
                              {shipmentStatuses.map((status) => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                              ))}
                            </select>
                          </label>
                          <button onClick={() => updateShipmentCarrier(shipment)}>Update carrier/tracking</button>
                        </footer>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'invoices' && (
              <section className="admin-stack">
                <div className="admin-table">
                  {invoices.map((invoice) => (
                    <div className="admin-card" key={invoice.id}>
                      <header className="card-header">
                        <h3>Invoice {invoice.id}</h3>
                        <span className={`status-pill status-${invoice.status}`}>{invoiceStatusMap[invoice.status || ''] || invoice.status || 'unknown'}</span>
                      </header>
                      <div className="card-body">
                        <dl>
                          <div><dt>Customer</dt><dd>{invoice.customerName || invoice.customerId || '—'}</dd></div>
                          <div><dt>Issued</dt><dd>{formatDate(invoice.issuedAt)}</dd></div>
                          <div><dt>Due</dt><dd>{formatDate(invoice.dueAt)}</dd></div>
                          <div><dt>Paid</dt><dd>{formatDate(invoice.paidAt)}</dd></div>
                          <div><dt>Total</dt><dd>{centsToCurrency(invoice.amountCents, invoice.currency || 'USD')}</dd></div>
                          {invoice.memo && <div><dt>Memo</dt><dd>{invoice.memo}</dd></div>}
                        </dl>
                      </div>
                      {canEditInvoices && (
                        <footer className="card-footer">
                          {invoice.status !== 'paid' ? (
                            <button onClick={() => markInvoiceStatus(invoice, 'paid')}>Mark paid</button>
                          ) : (
                            <button onClick={() => markInvoiceStatus(invoice, 'open')}>Reopen</button>
                          )}
                          <label>
                            <span>Status</span>
                            <select value={invoice.status || ''} onChange={(event) => markInvoiceStatus(invoice, event.target.value)}>
                              {invoiceStatuses.map((status) => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                              ))}
                            </select>
                          </label>
                        </footer>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'customers' && (
              <section className="admin-stack">
                <div className="admin-table">
                  {customers.map((customer) => (
                    <div className="admin-card" key={customer.id}>
                      <header className="card-header">
                        <h3>{customer.fullName || customer.email}</h3>
                        <span className={`status-pill status-${customer.status}`}>{customer.status}</span>
                      </header>
                      <div className="card-body">
                        <dl>
                          <div><dt>Email</dt><dd>{customer.email || '—'}</dd></div>
                          <div><dt>Phone</dt><dd>{customer.phone || '—'}</dd></div>
                          <div><dt>Tier</dt><dd>{customer.subscriptionTier || '—'}</dd></div>
                          <div><dt>Last intake</dt><dd>{formatDate(customer.lastIntakeAt)}</dd></div>
                          <div><dt>Balance</dt><dd>{centsToCurrency(customer.outstandingBalanceCents)}</dd></div>
                          <div><dt>Active shipments</dt><dd>{customer.activeShipments}</dd></div>
                          <div><dt>Overdue invoices</dt><dd>{customer.overdueInvoices}</dd></div>
                        </dl>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'formulas' && (
              <section className="admin-stack">
                {canEditFormulas && (
                  <div className="admin-grid">
                    <div className="admin-card">
                      <h3>Add supplier</h3>
                      <form className="admin-form" onSubmit={submitNewSupplier}>
                        <label>
                          <span>Name*</span>
                          <input
                            value={newSupplier.name}
                            onChange={(event) => setNewSupplier((prev) => ({ ...prev, name: event.target.value }))}
                            required
                          />
                        </label>
                        <label>
                          <span>Contact email</span>
                          <input
                            value={newSupplier.contactEmail}
                            onChange={(event) => setNewSupplier((prev) => ({ ...prev, contactEmail: event.target.value }))}
                            type="email"
                          />
                        </label>
                        <label>
                          <span>Origin region</span>
                          <input
                            value={newSupplier.originRegion}
                            onChange={(event) => setNewSupplier((prev) => ({ ...prev, originRegion: event.target.value }))}
                          />
                        </label>
                        <button type="submit">Save supplier</button>
                      </form>
                    </div>

                    <div className="admin-card">
                      <h3>Add formula</h3>
                      <form className="admin-form" onSubmit={submitNewFormula}>
                        <label>
                          <span>Name*</span>
                          <input
                            value={newFormula.name}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, name: event.target.value }))}
                            required
                          />
                        </label>
                        <label>
                          <span>Slug</span>
                          <input
                            value={newFormula.slug}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, slug: event.target.value }))}
                          />
                        </label>
                        <label>
                          <span>Supplier</span>
                          <select
                            value={newFormula.supplierId}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, supplierId: event.target.value }))}
                          >
                            <option value="">Select supplier</option>
                            {supplierOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Category</span>
                          <input
                            value={newFormula.category}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, category: event.target.value }))}
                          />
                        </label>
                        <label>
                          <span>Pattern</span>
                          <input
                            value={newFormula.pattern}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, pattern: event.target.value }))}
                          />
                        </label>
                        <label>
                          <span>Tagline</span>
                          <input
                            value={newFormula.tagline}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, tagline: event.target.value }))}
                          />
                        </label>
                        <label>
                          <span>Recommended tier</span>
                          <select
                            value={newFormula.recommendedTier}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, recommendedTier: event.target.value }))}
                          >
                            <option value="foundations">Foundations</option>
                            <option value="deep-dive">Deep dive</option>
                            <option value="concierge">Concierge</option>
                          </select>
                        </label>
                        <label>
                          <span>Status</span>
                          <select
                            value={newFormula.stockStatus}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, stockStatus: event.target.value }))}
                          >
                            <option value="active">Active</option>
                            <option value="backorder">Backorder</option>
                            <option value="qa-hold">QA hold</option>
                            <option value="retired">Retired</option>
                          </select>
                        </label>
                        <label>
                          <span>Hero ingredients</span>
                          <input
                            placeholder="Shu Di Huang, Fu Zi"
                            value={newFormula.heroIngredients}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, heroIngredients: event.target.value }))}
                          />
                        </label>
                        <label>
                          <span>Caution</span>
                          <textarea
                            value={newFormula.caution}
                            onChange={(event) => setNewFormula((prev) => ({ ...prev, caution: event.target.value }))}
                          />
                        </label>
                        <button type="submit">Save formula</button>
                      </form>
                    </div>
                  </div>
                )}

                <div className="admin-table">
                  {formulas.map((formula) => (
                    <div className="admin-card" key={formula.id}>
                      <header className="card-header">
                        <h3>{formula.name}</h3>
                        <span className={`status-pill status-${formula.stockStatus || 'active'}`}>
                          {formulaStatusMap[formula.stockStatus || 'active'] || formula.stockStatus || 'active'}
                        </span>
                      </header>
                      <div className="card-body">
                        <dl>
                          <div><dt>Slug</dt><dd>{formula.slug || '—'}</dd></div>
                          <div><dt>Category</dt><dd>{formula.category || '—'}</dd></div>
                          <div><dt>Pattern</dt><dd>{formula.pattern || '—'}</dd></div>
                          <div><dt>Supplier</dt><dd>{formula.supplierName || '—'}</dd></div>
                          <div><dt>Hero ingredients</dt><dd>{formula.heroIngredients.length ? formula.heroIngredients.join(', ') : '—'}</dd></div>
                          {formula.caution && <div><dt>Caution</dt><dd>{formula.caution}</dd></div>}
                          {formula.tagline && <div><dt>Tagline</dt><dd>{formula.tagline}</dd></div>}
                          {formula.description && <div><dt>Description</dt><dd>{formula.description}</dd></div>}
                        </dl>
                      </div>
                      {canEditFormulas && (
                        <footer className="card-footer">
                          <label>
                            <span>Status</span>
                            <select value={formula.stockStatus || 'active'} onChange={(event) => updateFormulaStatus(formula, event.target.value)}>
                              <option value="active">Active</option>
                              <option value="backorder">Backorder</option>
                              <option value="qa-hold">QA hold</option>
                              <option value="retired">Retired</option>
                            </select>
                          </label>
                          <label>
                            <span>Supplier</span>
                            <select value={formula.supplierId || ''} onChange={(event) => updateFormulaSupplier(formula, event.target.value)}>
                              <option value="">Unassigned</option>
                              {supplierOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>
                          <button onClick={() => updateFormulaHero(formula)}>Update hero ingredients</button>
                          <button onClick={() => updateFormulaCaution(formula)}>Update caution</button>
                        </footer>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'support' && (
              <section className="admin-stack">
                <div className="admin-grid">
                  <div className="admin-card">
                    <h3>Filters</h3>
                    <div className="admin-form">
                      <label>
                        <span>Status</span>
                        <select
                          value={ticketFilters.status}
                          onChange={(event) => setTicketFilters((prev) => ({ ...prev, status: event.target.value }))}
                        >
                          <option value="">All</option>
                          {statusOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Priority</span>
                        <select
                          value={ticketFilters.priority}
                          onChange={(event) => setTicketFilters((prev) => ({ ...prev, priority: event.target.value }))}
                        >
                          <option value="">All</option>
                          {priorityOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                      <button type="button" onClick={() => refreshTickets()}>Refresh</button>
                    </div>
                  </div>

                  {canManageSupport && (
                    <div className="admin-card">
                      <h3>Open ticket</h3>
                      <form className="admin-form" onSubmit={submitNewTicket}>
                        <label>
                          <span>Subject*</span>
                          <input value={newTicket.subject} onChange={(event) => setNewTicket((prev) => ({ ...prev, subject: event.target.value }))} required />
                        </label>
                        <label>
                          <span>Customer ID</span>
                          <input value={newTicket.customerId} onChange={(event) => setNewTicket((prev) => ({ ...prev, customerId: event.target.value }))} />
                        </label>
                        <label>
                          <span>Category</span>
                          <input value={newTicket.category} onChange={(event) => setNewTicket((prev) => ({ ...prev, category: event.target.value }))} />
                        </label>
                        <label>
                          <span>Priority</span>
                          <select value={newTicket.priority} onChange={(event) => setNewTicket((prev) => ({ ...prev, priority: event.target.value }))}>
                            {priorityOptions.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Initial note</span>
                          <textarea value={newTicketMessage} onChange={(event) => setNewTicketMessage(event.target.value)} />
                        </label>
                        <button type="submit">Create ticket</button>
                      </form>
                    </div>
                  )}
                </div>

                <div className="admin-support-layout">
                  <div className="admin-table">
                    {tickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        className={`admin-card ticket-card ${ticketDetail?.ticket.id === ticket.id ? 'active' : ''}`}
                        onClick={() => loadTicketDetail(ticket.id)}
                      >
                        <header className="card-header">
                          <div>
                            <h3>{ticket.subject}</h3>
                            <span className="ticket-meta">{ticket.customerName || ticket.customerEmail || ticket.customerId || 'Unassigned'}</span>
                          </div>
                          <div className="ticket-pills">
                            <span className={`status-pill status-${ticket.status}`}>{ticket.status}</span>
                            <span className={`status-pill priority-${ticket.priority}`}>{ticket.priority}</span>
                          </div>
                        </header>
                        <p className="ticket-snippet">{ticket.lastMessage || 'No replies yet'}</p>
                        <footer className="ticket-footer">
                          <span>Assigned: {ticket.assignedTo || 'Unassigned'}</span>
                          <span>Updated: {formatDate(ticket.updatedAt)}</span>
                        </footer>
                      </button>
                    ))}
                  </div>

                  {ticketDetail && (
                    <div className="admin-card ticket-detail">
                      <header className="card-header">
                        <div>
                          <h3>{ticketDetail.ticket.subject}</h3>
                          <p className="ticket-meta">{ticketDetail.ticket.customerName || ticketDetail.ticket.customerEmail || ticketDetail.ticket.customerId || 'Unauthenticated'}</p>
                        </div>
                        {canManageSupport && (
                          <div className="ticket-controls">
                            <label>
                              <span>Status</span>
                              <select value={ticketDetail.ticket.status} onChange={(event) => updateTicket(ticketDetail.ticket.id, { status: event.target.value })}>
                                {statusOptions.map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>Priority</span>
                              <select value={ticketDetail.ticket.priority} onChange={(event) => updateTicket(ticketDetail.ticket.id, { priority: event.target.value })}>
                                {priorityOptions.map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>Assignee</span>
                              <input
                                value={ticketDetail.ticket.assignedTo || ''}
                                onChange={(event) => updateTicket(ticketDetail.ticket.id, { assignedTo: event.target.value })}
                                placeholder="moderator_soupcon"
                              />
                            </label>
                          </div>
                        )}
                      </header>

                      <div className="ticket-thread">
                        {ticketDetail.messages.map((message) => (
                          <article className={`ticket-message ${message.visibility !== 'public' ? 'internal' : ''}`} key={message.id}>
                            <header>
                              <strong>{message.authorRole || 'system'}</strong>
                              <span>{formatDate(message.createdAt)}</span>
                              {message.visibility !== 'public' && <span className="visibility-pill">Internal</span>}
                            </header>
                            <p>{message.body}</p>
                          </article>
                        ))}
                      </div>

                      {canManageSupport && (
                        <form
                          className="admin-form ticket-reply"
                          onSubmit={(event) => {
                            event.preventDefault();
                            postTicketReply(ticketDetail.ticket.id, newResponse, 'public');
                          }}
                        >
                          <label>
                            <span>Reply</span>
                            <textarea value={newResponse} onChange={(event) => setNewResponse(event.target.value)} placeholder="Add response..." />
                          </label>
                          <div className="ticket-reply-actions">
                            <button type="submit">Send reply</button>
                            <button
                              type="button"
                              onClick={() => {
                                const note = prompt('Internal note') || '';
                                if (note.trim()) {
                                  postTicketReply(ticketDetail.ticket.id, note, 'internal');
                                }
                              }}
                            >
                              Add internal note
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminApp;
